import EventBus from '../services/service.eventBus';
import { fetchQuestions } from '../services/service.questions';
import { state as aftermarketState } from '../store/store.aftermarket';
import { resetAll } from '../store/store.all';
import { state as answerState } from '../store/store.answers';
import { state as dynamicEntryState } from '../store/store.dynamic-entry';
import { state as exteriorState } from '../store/store.exterior';
import { state as historyState } from '../store/store.history';
import { state as interiorState } from '../store/store.interior';
import { state as mechanicalState } from '../store/store.mechanical';
import { state as offerState } from '../store/store.offer';
import { state as questionVehicleState } from '../store/store.question-vehicle';
import { state as replacementState } from '../store/store.replacement';
import { state as supplementState } from '../store/store.supplement';
import { state as vehicleConfigState } from '../store/store.vehicle-config';
import { isString } from '../utils/utils.js';
import {
  QUESTION_CONDITION_TIER_SELECTION,
  QUESTION_ODORS,
  QUESTION_ORIGINAL_OWNER,
  QUESTION_VEHICLE_AUCTION,
  QUESTION_VEHICLE_ISSUES,
  QUESTION_VEHICLE_RENTAL,
  addOtherQuestions,
  autocheckDataQuestions,
  getInteriorQuestionProps,
  getItemSubset,
  getItemsByCanonicalName,
  getSectionFromItems,
  getSubsectionGroup,
  pluckQuestion,
  toggleModificationsQuestion,
  twoKeysStaticQuestion,
} from '../utils/utils.question';
import { getNumberOfRows, vehicleBodyType } from '../utils/utils.vehicle-view';

import { clearAnswers, loadAnswersFromBrowser, loadAnswersFromQuestions, onStateFieldAnswered, resetStateAnswers } from './actions.answers';
import { checkVehicleEligibility } from './actions.eligibility';
import offerVehicleActions from './actions.offer-vehicle';

export const onQuestionsInit = async (vehicleDetails, allowIneligibleVehicleSubmission: boolean = false) => {
  EventBus.publish('QuestionsInit', vehicleDetails);
  try {
    const isIneligibleVehicleAllowed = allowIneligibleVehicleSubmission || dynamicEntryState.allowIneligibleVehicleSubmission;
    const vehicleDataForQuestionsFetch = getVehicleDataForQuestionsInit(vehicleDetails);
    //  Check eligibility before making documents call. This prevent ineligible vehicles from causing logged errors
    const { trimId, mileage, engineId, drivetrainId, transmissionId } = vehicleDataForQuestionsFetch;
    const isEligible = await checkVehicleEligibility(trimId, mileage, engineId, drivetrainId, transmissionId);

    if (isEligible || isIneligibleVehicleAllowed) {
      const { questionsGroupedByCanonical, trimInformation, vehicleInformationResponse, documentsApiFailed } = await fetchQuestions({ ...vehicleDataForQuestionsFetch });

      vehicleConfigState.documentsApiFailed = documentsApiFailed;

      onQuestionsInitSuccess({ questionsGroupedByCanonical, trimInformation, vehicleInformationResponse, vehicleDetails });
      offerVehicleActions.onVehicleDetailsSet(vehicleDetails);
    } else {
      onQuestionsInitFail('VehicleIsNotIcoEligible');
    }
  } catch (error) {
    onQuestionsInitFail(error);
  }
};

const getVehicleDataForQuestionsInit = (vehicleDetails) => {
  return {
    vin: vehicleDetails.vin !== 0 ? vehicleDetails.vin : answerState['vehicle'].vin,
    mileage: vehicleDetails.mileage !== 0 ? vehicleDetails.mileage : answerState['vehicle'].mileage,
    yearId: vehicleDetails.yearId !== 0 ? vehicleDetails.yearId : answerState['vehicle'].yearId,
    makeId: vehicleDetails.makeId !== 0 ? vehicleDetails.makeId : answerState['vehicle'].makeId,
    modelId: vehicleDetails.modelId !== 0 ? vehicleDetails.modelId : answerState['vehicle'].modelId,
    trimId: vehicleDetails.trimId !== 0 ? vehicleDetails.trimId : answerState['vehicle'].trimId,
    colorId: vehicleDetails.colorId !== 0 ? vehicleDetails.colorId : answerState['vehicle'].colorId,
    transmissionId: vehicleDetails.transmissionId !== 0 ? vehicleDetails.transmissionId : answerState['vehicle'].transmissionId,
    engineId: vehicleDetails.engineId !== 0 ? vehicleDetails.engineId : answerState['vehicle'].engineId,
    drivetrainId: vehicleDetails.drivetrainId !== 0 ? vehicleDetails.drivetrainId : answerState['vehicle'].drivetrainId,
  };
};

const getOdorsQuestion = (questionSet) => {
  const odorsQuestion = pluckQuestion(questionSet, QUESTION_ODORS);
  // Original label from API : Does your vehicle have any noticeable odors (maybe from smoking, pets or mold)?
  odorsQuestion.label = 'Has noticeable odors';
  odorsQuestion.itemHeader = 'Odors';
  odorsQuestion.itemDescription = '(maybe from smoking, pets or mold)';
  return odorsQuestion;
};

export const onQuestionsInitSuccess = ({ questionsGroupedByCanonical, trimInformation, vehicleInformationResponse, vehicleDetails }) => {
  // All condition quiz answers
  EventBus.publish('QuestionsInitSuccess', {});

  // Page Sections
  let allConditions = getItemsByCanonicalName(questionsGroupedByCanonical, 'conditions');
  let allHistory = getItemsByCanonicalName(questionsGroupedByCanonical, 'history');
  const allReplacement = getItemsByCanonicalName(questionsGroupedByCanonical, 'replacementVehicle');
  const allOther = [...getItemsByCanonicalName(questionsGroupedByCanonical, 'other'), ...addOtherQuestions, ...autocheckDataQuestions];

  // one off for odors question, moved to interior condition questions
  const odorsQuestion = getOdorsQuestion(allHistory);
  const supplement = getItemsByCanonicalName(questionsGroupedByCanonical, 'replacementVehicle/supplement');
  allConditions = [...allConditions, { ...odorsQuestion, item: 'Odors', isRequired: true, ...getInteriorQuestionProps() }];
  allHistory = allHistory.filter((question) => question.questionId !== QUESTION_ODORS);

  onInteriorQuestionsSet(allConditions);
  onExteriorQuestionsSet(allConditions);
  onMechanicalQuestionsSet(allConditions);
  onAftermarketQuestionsSet(allConditions); // Add keys questions into here...
  onHistoryQuestionsSet(allHistory);
  onSupplementSet(supplement);

  // Try and load answers from browser storage, if we don't find anything, generate structure from questions.
  try {
    loadAnswersFromBrowser();

    if (Number(answerState.vehicle.trimId) !== 0 && Number(answerState.vehicle.trimId) !== Number(vehicleDetails.trimId)) {
      console.log('Stored answers are for a different vehicle, resetting');
      resetStateAnswers();
      clearAnswers();

      // Using the questions from historyState, since that does a lot of processing as far as what we actually ask vs what was returned from the API
      loadAnswersFromQuestions({
        conditions: [...allConditions, toggleModificationsQuestion],
        history: [...historyState.questions, twoKeysStaticQuestion], // add keys question here so that it would be collected. Currently, keys question is on keys-modifications
        replacement: allReplacement,
        other: allOther,
      });
    }
  } catch (error) {
    console.log('Unable to load answers from browser store', error);
    // Using the questions from historyState, since that does a lot of processing as far as what we actually ask vs what was returned from the API
    loadAnswersFromQuestions({
      conditions: [...allConditions, toggleModificationsQuestion],
      history: [...historyState.questions, twoKeysStaticQuestion], // add keys question here so that it would be collected. Currently, keys question is on keys-modifications
      replacement: allReplacement,
      other: allOther,
    });
  }

  // Vehicle information
  // Set after the answers is set up so any changes will get synced there
  onVehicleInfoLoadFromQuestions(trimInformation, vehicleInformationResponse, vehicleDetails);
};

export const onQuestionsInitFail = (error) => {
  EventBus.publish('QuestionsInitFail', error);
  console.error('QuestionsInitFail', error);
  vehicleConfigState.documentsApiFailed = true; // set documentsApiFailed to true so that the error is shown on the vehicle config page
  // clear answers storage if there is failure and clear stores
  console.log('Clearing saved answers and resetting stores on question init failure');
  clearAnswers();
  resetAll();
};

export const onInteriorQuestionsSet = (questions) => {
  // Interior
  const interiorConditionSubset = getSubsectionGroup(questions, 'Interior Wear or Damage');

  interiorState.questionsDashboard = getItemSubset(interiorConditionSubset, 'Dashboard');
  interiorState.questionsFrontSeats = getItemSubset(interiorConditionSubset, 'Front Seats');
  interiorState.questionsRearSeats = getItemSubset(interiorConditionSubset, 'Rear Seats');
  interiorState.questionsCargoBed = getItemSubset(interiorConditionSubset, 'Cargo Bed');
  interiorState.questionsHeadliner = getItemSubset(interiorConditionSubset, 'Headliner');
  interiorState.questionsCarpet = getItemSubset(interiorConditionSubset, 'Carpet');
  interiorState.questionsMoldingTrim = getItemSubset(interiorConditionSubset, 'Molding/Trim');
  // moved here from history "questionId": "qp/65"
  interiorState.questionsOdors = getItemSubset(interiorConditionSubset, 'Odors');

  const warningLightsConditionSubset = getSubsectionGroup(questions, 'Warning Lights');
  interiorState.questionsDashboardLights = warningLightsConditionSubset;
};

export const onExteriorQuestionsSet = (questions) => {
  const wheelsBrakes = getSubsectionGroup(questions, 'Wheels & Brakes');
  const driverSide = getSubsectionGroup(questions, "Driver's Side Wear or Damage");
  const passengerSide = getSubsectionGroup(questions, 'Passenger Side Wear or Damage');
  const front = getSubsectionGroup(questions, 'Front Wear or Damage');
  const rear = getSubsectionGroup(questions, 'Rear Wear or Damage');
  const windowsLights = [...getSubsectionGroup(questions, 'Windshield & Glass'), ...getSubsectionGroup(questions, 'Windshield Glass')];

  // Front
  exteriorState.questionsHood = getItemSubset(front, 'Hood');
  exteriorState.questionsGrille = getItemSubset(front, 'Grille');
  exteriorState.questionsFrontBumper = getItemSubset(front, 'Front Bumper');
  exteriorState.questionsFrontMoldingTrim = getItemSubset(front, 'Molding/Trim');

  // Driver
  exteriorState.questionsDriverFrontFender = getItemSubset(driverSide, 'Fender');
  exteriorState.questionsDriverFrontDoor = getItemSubset(driverSide, 'Front Door');
  exteriorState.questionsDriverRearDoor = getItemSubset(driverSide, 'Rear Door');
  exteriorState.questionsDriverRearPanel = getItemSubset(driverSide, 'Quarter Panel');
  exteriorState.questionsDriverRockerPanel = getItemSubset(driverSide, 'Rocker Panel');
  exteriorState.questionsDriverMoldingTrim = getItemSubset(driverSide, 'Molding/Trim');

  // Passenger
  exteriorState.questionsPassengerFrontFender = getItemSubset(passengerSide, 'Fender');
  exteriorState.questionsPassengerFrontDoor = getItemSubset(passengerSide, 'Front Door');
  exteriorState.questionsPassengerRearDoor = getItemSubset(passengerSide, 'Rear Door');
  exteriorState.questionsPassengerRearPanel = getItemSubset(passengerSide, 'Quarter Panel');
  exteriorState.questionsPassengerRockerPanel = getItemSubset(passengerSide, 'Rocker Panel');
  exteriorState.questionsPassengerMoldingTrim = getItemSubset(passengerSide, 'Molding/Trim');

  // Wheels and Brakes
  exteriorState.questionsTiresRims = getItemSubset(wheelsBrakes, 'Tires & Rims');
  exteriorState.questionsBrakes = getItemSubset(wheelsBrakes, 'Brakes');

  // Rear
  exteriorState.questionsRoof = getItemSubset(rear, 'Roof');
  exteriorState.questionsTrunk = getItemSubset(rear, 'Trunk');
  if (exteriorState.questionsTrunk.length < 1) {
    exteriorState.questionsTrunk = getItemSubset(rear, 'Tailgate');
  }
  if (exteriorState.questionsTrunk.length < 1) {
    exteriorState.questionsTrunk = getItemSubset(rear, 'Liftgate');
  }
  exteriorState.questionsRearBumper = getItemSubset(rear, 'Rear Bumper');
  exteriorState.questionsRearMoldingTrim = getItemSubset(rear, 'Molding/Trim');

  // Windows/Lights
  exteriorState.questionsHeadlights = getItemSubset(windowsLights, 'Headlights');
  exteriorState.questionsMirrors = getItemSubset(windowsLights, 'Mirrors');
  exteriorState.questionsRearWindow = getItemSubset(windowsLights, 'Rear Window');
  exteriorState.questionsSideWindows = getItemSubset(windowsLights, 'Side Windows'); // hmm
  exteriorState.questionsTaillights = getItemSubset(windowsLights, 'Taillights');
  exteriorState.questionsWindshield = getItemSubset(windowsLights, 'Windshield');
};

export const onMechanicalQuestionsSet = (questions) => {
  const mechanicalConditionsSubset = getSubsectionGroup(questions, 'Mechanical Issues');

  mechanicalState.questionsFrameStructural = getItemSubset(mechanicalConditionsSubset, 'Frame & Structural Damage');
  mechanicalState.questionsEngine = getItemSubset(mechanicalConditionsSubset, 'Engine');
  mechanicalState.questionsTransmission = getItemSubset(mechanicalConditionsSubset, 'Transmission');
  mechanicalState.questionsSteering = getItemSubset(mechanicalConditionsSubset, 'Steering');
  mechanicalState.questionsExhaust = getItemSubset(mechanicalConditionsSubset, 'Exhaust');
  mechanicalState.questionsSuspension = getItemSubset(mechanicalConditionsSubset, 'Suspension');
  mechanicalState.questionsHeatingAir = getItemSubset(mechanicalConditionsSubset, 'Heating & Air Conditioning');
  mechanicalState.questionsBatteryElectrical = getItemSubset(mechanicalConditionsSubset, 'Battery/Electrical');
  mechanicalState.questionsStereo = getItemSubset(mechanicalConditionsSubset, 'Stereo');
  mechanicalState.questionsOdometer = getItemSubset(mechanicalConditionsSubset, 'Odometer');
  mechanicalState.questionsSunMoonRoof = getItemSubset(mechanicalConditionsSubset, 'Sunroof/Moonroof');
  mechanicalState.questionsAirbags = getItemSubset(mechanicalConditionsSubset, 'Airbags');
};

export const onAftermarketQuestionsSet = (questions) => {
  const aftermarketSection = getSectionFromItems(questions, 'Aftermarket');
  // Aftermarket
  const aftermarketStereoItems = getItemSubset(aftermarketSection, 'Stereo');
  const aftermarketWheelsTiresItems = getItemSubset(aftermarketSection, 'Wheels & Tires');
  const aftermarketSuspensionItems = getItemSubset(aftermarketSection, 'Suspension');
  const aftermarketExhaustItems = getItemSubset(aftermarketSection, 'Exhaust');
  const aftermarketPerformanceItems = getItemSubset(aftermarketSection, 'Performance Modifications');

  // Page question sets
  // Aftermarket questions sets
  aftermarketState.questionsAftermarket = aftermarketSection; // only afterMarket questions
  aftermarketState.stereoQuestions = aftermarketStereoItems;
  aftermarketState.wheelsTiresQuestions = aftermarketWheelsTiresItems;
  aftermarketState.suspensionQuestions = aftermarketSuspensionItems;
  aftermarketState.exhaustQuestions = aftermarketExhaustItems;
  aftermarketState.performanceQuestions = aftermarketPerformanceItems;
  aftermarketState.twoKeysQuestions = [twoKeysStaticQuestion]; // add keys questions
  aftermarketState.toggleModificationsQuestion = [toggleModificationsQuestion]; // add toggle question
};

export const onHistoryQuestionsSet = (questions) => {
  historyState.questionsHistory = [...questions];
};

export const onReplacementQuestionsSet = (questions) => {
  replacementState.questionsReplacement = [...questions];
};

export const onSupplementSet = (data) => {
  supplementState.makesModels = data.makesModels;
  supplementState.yearRange = data.yearRange;
};

const filterRenameVehicleHistory = (historyQuestions = []) => {
  // filter out only the questions we care about and give the questions shorter text
  const visibleQuestions = new Map([
    [QUESTION_ODORS, 'Smoking/Other Odors'],
    [QUESTION_VEHICLE_AUCTION, 'Auctioned in Last 45 Days'],
    [QUESTION_ORIGINAL_OWNER, 'Original Owner'],
    [QUESTION_VEHICLE_ISSUES, 'Accidents'],
    [QUESTION_VEHICLE_RENTAL, 'Rental Vehicle'],
  ]);

  function cleanHistoryValue(historyValue): string {
    // history answer values comes in as both boolean and Yes/No (!!) depending on question -_-
    // we want to display Yes/No to user
    if (isString(historyValue)) {
      return historyValue;
    }
    return !!historyValue ? 'Yes' : 'No';
  }

  return historyQuestions
    .filter((h) => visibleQuestions.has(h.questionId))
    .map((ch) => ({
      questionId: ch.questionId,
      label: visibleQuestions.get(ch.questionId),
      value: cleanHistoryValue(ch.value),
    }));
};

export const onOfferLoad = (offer) => {
  const { prospect } = offer;

  const vehicleConditions = getSectionFromItems(prospect.conditions, 'Vehicle Condition');
  const afterMarketConditions = getSectionFromItems(prospect.conditions, 'Aftermarket');
  const vehicleHistory = filterRenameVehicleHistory(prospect.history);

  // Insert condition tier question if selection was made
  addConditionTierSelection(vehicleConditions, offer);

  const amSubsections = Array.from(
    new Set(
      afterMarketConditions.map((c) => {
        return c.subsection;
      })
    )
  );

  const vcSubsections = Array.from(
    new Set(
      vehicleConditions.map((c) => {
        return c.subsection;
      })
    )
  );

  const conditionsByGroup = vcSubsections.reduce((acc, section: string) => {
    acc[section] = getSubsectionGroup(vehicleConditions, section);
    return acc;
  }, {});

  // Modify Warning Lights labels to show "stays on" instead of the light label for view purposes
  if (conditionsByGroup.hasOwnProperty('Warning Lights')) {
    conditionsByGroup['Warning Lights'] = conditionsByGroup['Warning Lights'].map((wl) => ({ ...wl, label: 'Stays On' }));
  }

  // single + multi
  const aftermarketGroup = amSubsections.reduce((acc, section: string) => {
    acc[section] = getSubsectionGroup(afterMarketConditions, section).map((q) => {
      switch (q.questionType) {
        case 'SingleSelect':
          const selected = q.selectChoices.find((q) => q.selected);
          return { ...q, label: q.label, answers: [{ value: selected.value, comment: selected.comment }] };
        case 'MultiSelect':
          const answers = q.selectChoices.filter((q) => q.selected).map((c) => ({ value: c.value, comment: c.comment }));
          return { ...q, label: q.label, answers };

        case 'ToggleSelect':
          return { ...q, label: q.item, answers: [{ value: q.label, comment: q.comment }] };

        case 'Integer':
        default:
          return { ...q, label: q.label, answers: [{ value: q.value, comment: q.comment }] };
      }
    });
    return acc;
  }, {});

  offerState.vehicleHistory = vehicleHistory.filter((q) => q.questionId !== QUESTION_ODORS);
  offerState.vehicleOdor = vehicleHistory.filter((q) => q.questionId === QUESTION_ODORS);
  offerState.vehicleConditions = cleanOfferVehicleConditions(conditionsByGroup);
  offerState.vehicleModifications = cleanOfferVehicleModifications(aftermarketGroup);

  EventBus.publish('OfferLoadConditions', conditionsByGroup);
  EventBus.publish('OfferLoadModifications', aftermarketGroup);
};

const cleanOfferVehicleModifications = (modifications) => {
  const modificationSections = Object.keys(modifications);
  if (modificationSections.length > 0) {
    return modificationSections.reduce((acc, m) => {
      const sectionQuestions = modifications[m];
      const getAnswers = sectionQuestions.map((q) => {
        const checkType = () => {
          if (q.questionType === 'MultiSelect') {
            return q.answers.map((ans, i) => {
              return {
                label: i === 0 ? q.item : '', // only show the label for the first answer to avoid repetition
                value: ans.value,
                comment: ans.comment,
              };
            });
          }
          return q.answers.map((ans) => {
            return {
              label: q.label,
              value: ans.value,
              comment: ans.comment,
            };
          });
        };
        return { questionType: q.questionType, answers: checkType() };
      });
      return [...acc, { header: m, answers: [...getAnswers] }];
    }, []);
  }
  return [];
};

const cleanOfferVehicleConditions = (conditions) => {
  const conditionsSections = Object.keys(conditions);
  return conditionsSections.reduce((acc, c) => {
    const sectionQuestions = conditions[c];
    const getAnswers = sectionQuestions.map((q) => {
      const checkType = () => {
        if (q.questionType === 'MultiSelect') {
          return q.selectChoices.filter((ch) => ch.selected).map((ch) => ({ value: ch.value, comment: ch.comment }));
        } else if (q.questionType === 'SingleSelect') {
          return q.selectChoices.filter((ch) => ch.selected).map((ch) => ({ label: q.label, value: ch.value, comment: ch.comment }));
        } else if (q.questionType === 'ToggleSelect') {
          return [{ label: q.item, value: q.label, comment: q.comment }];
        }
        return [{ label: q.label, value: q.value, comment: q.comment }];
      };
      return { questionType: q.questionType, itemHeader: q.itemHeader, answers: checkType() };
    });
    return [...acc, { header: c, answers: [...getAnswers] }];
  }, []);
};

// Loads the vehicle information we received from the question API
const onVehicleInfoLoadFromQuestions = (trimInformation, vehicleInformation, vehicleDetails) => {
  const { bodyStyle, modelName, displayName, makeName, trimId } = trimInformation;
  const { numberOfDoors, thirdRowSeats, maxSeatingCapacity } = vehicleInformation;
  const { makeId, modelId, yearId, drivetrain } = vehicleDetails;

  questionVehicleState.vehicleType = vehicleBodyType(bodyStyle.toLowerCase(), modelName.toLowerCase());
  questionVehicleState.numberOfDoors = numberOfDoors;
  questionVehicleState.numberOfRows = getNumberOfRows(thirdRowSeats, maxSeatingCapacity);
  questionVehicleState.isThirdRowOptional = thirdRowSeats;
  questionVehicleState.bodyStyle = bodyStyle;
  questionVehicleState.make = makeName;
  questionVehicleState.model = modelName;
  questionVehicleState.trim = displayName;
  questionVehicleState.trimId = trimId;
  questionVehicleState.makeId = makeId;
  questionVehicleState.modelId = modelId;
  questionVehicleState.yearId = yearId;
  questionVehicleState.drivetrain = drivetrain;

  // writing trimId here to the answer store so that we know which set of questions we are using even after page refresh
  // this will be overwritten by either the entry or the contact component
  onStateFieldAnswered('vehicle', 'trimId', trimId);

  EventBus.publish('onVehicleInfoLoadFromQuestions', { bodyStyle, modelName, numberOfDoors, thirdRowSeats, maxSeatingCapacity });
};

const addConditionTierSelection = (vehicleConditions = [], offer = { conditionTierSelection: '', conditionTierList: [] }) => {
  if (offer.conditionTierSelection) {
    vehicleConditions.length = 0;
    vehicleConditions.push({
      questionId: QUESTION_CONDITION_TIER_SELECTION,
      item: offer.conditionTierSelection,
      label: offer.conditionTierList.find((c) => c.name === offer.conditionTierSelection).description,
      section: 'Vehicle Condition',
      subsection: 'Condition Tier Selection',
      questionType: 'ToggleSelect',
      value: offer.conditionTierList.find((c) => c.name === offer.conditionTierSelection).description,
      acceptsComment: false,
      comment: '',
    });
  }
};

export default { onOfferLoad, onReplacementQuestionsSet };
